import React, { useState, useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { personIcon } from '../../assets/icons';
import { FaEllipsisV, FaTrash } from 'react-icons/fa';
import styles from './OwnedTab.module.css';
import CreatePersonaModal from '../utils/CreatePersonaModal';
import { api } from '../../api';
import ConfirmationDialog from './ConfirmationDialog';
import type { PersonaDetailResponse, CreatePersonaPayload } from '../../api/types';

// Update interface to match what CreatePersonaModal expects
interface PersonaSubmitData {
  name: string;
  description: string;
  icon: File | null;
}

interface OwnedTabPersonasProps {
  personas: PersonaDetailResponse[];
  isLoading: boolean;
  onRefresh: () => void;
}

function OwnedTabPersonas({ personas, isLoading, onRefresh }: OwnedTabPersonasProps): JSX.Element {
  const navigate = useNavigate();
  const [isModalOpen, setIsModalOpen] = useState<boolean>(false);
  const [menuOpen, setMenuOpen] = useState<string | null>(null);
  const [dialogOpen, setDialogOpen] = useState<boolean>(false);
  const [personaToDelete, setPersonaToDelete] = useState<PersonaDetailResponse | null>(null);
  const [isDeleting, setIsDeleting] = useState<boolean>(false);
  const menuRef = useRef<HTMLDivElement | null>(null);

  // Close menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (event: MouseEvent): void => {
      if (menuRef.current && !menuRef.current.contains(event.target as Node)) {
        setMenuOpen(null);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, [menuRef]);

  const handleCreateClick = (): void => {
    setIsModalOpen(true);
  };

  const handleCreateSubmit = async (data: PersonaSubmitData): Promise<void> => {
    try {
      await api.personas.create({
        display_name: data.name,
        description: data.description,
        icon: data.icon
      } as CreatePersonaPayload);
      setIsModalOpen(false);
      onRefresh();
    } catch (error) {
      console.error('Failed to create persona:', error);
      throw error;
    }
  };

  const toggleMenu = (e: React.MouseEvent, id: string): void => {
    e.stopPropagation();
    setMenuOpen(menuOpen === id ? null : id);
  };

  const openDeleteDialog = (e: React.MouseEvent, persona: PersonaDetailResponse): void => {
    e.stopPropagation();
    setMenuOpen(null);
    setPersonaToDelete(persona);
    setDialogOpen(true);
  };

  const handleDeletePersona = async (): Promise<void> => {
    if (!personaToDelete) return;
    
    setIsDeleting(true);
    
    try {
      // Get the persona instance and delete it
      const persona = await api.personas.get(personaToDelete.id);
      await persona.delete();
      
      // Refresh the personas list
      onRefresh();
    } catch (error) {
      console.error('Failed to delete persona:', error);
      setIsDeleting(false);
      // Could add a toast notification here for error feedback
    }
  };

  const handlePersonaClick = (personaId: string): void => {
    navigate(`/personas/${personaId}`);
  };

  if (isLoading) {
    return <div className={styles.loading}>Loading...</div>;
  }

  if (personas.length === 0) {
    return (
      <>
        <div className={styles.emptyState}>
          <p>No personas yet</p>
          <button 
            className={styles.createButton}
            onClick={handleCreateClick}
          >
            Create Persona
          </button>
        </div>
        <CreatePersonaModal
          isOpen={isModalOpen}
          onClose={() => setIsModalOpen(false)}
          onSubmit={handleCreateSubmit}
        />
      </>
    );
  }

  return (
    <>
      <div className={styles.tabContent}>
        <div className={styles.scrollContainer}>
          <div className={styles.listContent}>
            <button 
              className={styles.createButton}
              onClick={handleCreateClick}
            >
              Create Persona
            </button>
            {personas.map((persona) => (
              <div 
                key={persona.id} 
                className={styles.listItem}
                onClick={() => handlePersonaClick(persona.id)}
              >
                <div className={styles.itemImage}>
                  <img src={persona.icon || personIcon} alt={persona.display_name} />
                </div>
                <div className={styles.itemContent}>
                  <div className={styles.itemHeader}>
                    <h3>{persona.display_name}</h3>
                    <div 
                      className={styles.menuContainer} 
                      onClick={(e) => e.stopPropagation()}
                      ref={menuOpen === `persona-${persona.id}` ? menuRef : null}
                    >
                      <button 
                        className={styles.menuButton} 
                        onClick={(e) => toggleMenu(e, `persona-${persona.id}`)}
                        aria-label="Persona options"
                      >
                        <FaEllipsisV />
                      </button>
                      {menuOpen === `persona-${persona.id}` && (
                        <div className={styles.menuDropdown}>
                          <button 
                            className={styles.menuItem}
                            onClick={(e) => openDeleteDialog(e, persona)}
                            disabled={persona.is_default}
                            title={persona.is_default ? "Cannot delete default persona" : "Delete persona"}
                          >
                            <FaTrash className={styles.menuIcon} />
                            <span>Delete persona</span>
                          </button>
                        </div>
                      )}
                    </div>
                  </div>
                  <p className={styles.tagline}>{persona.description}</p>
                  {persona.is_default && <span className={styles.username}>Default</span>}
                </div>
              </div>
            ))}
          </div>
        </div>
      </div>
      <CreatePersonaModal
        isOpen={isModalOpen}
        onClose={() => setIsModalOpen(false)}
        onSubmit={handleCreateSubmit}
      />
      <ConfirmationDialog
        isOpen={dialogOpen}
        onClose={() => {
          setDialogOpen(false);
          setPersonaToDelete(null);
        }}
        onConfirm={handleDeletePersona}
        title="Delete Persona"
        message={`Are you sure you want to delete ${personaToDelete?.display_name || 'this persona'}? This action cannot be undone.`}
        confirmText="Delete"
        cancelText="Cancel"
        isDestructive={true}
      />
    </>
  );
}

export default OwnedTabPersonas; 